import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Form, Input, Button, Dropdown } from 'semantic-ui-react';
import i18next from 'i18next';
import MenuTaxSetup from './MenuTaxSetup';
import { AvailableDaysEditor, ImageInput } from '../../common';
import { saveMenuCategory, editEntityField, uploadImage } from '../../../actions';
import convertToCategoryEntity from '../../../entities/menuCategory';

class MenuCategoryEditor extends Component {
    handleSubmit = (event) => {
        const { menuId, ...category } = this.props.entity;
        const { childCategories, branchIdForOverride } = this.props;
        const shouldSkipApi = ['addon', 'mandatory'].includes(category.type) ? this.props.linkTo : undefined;
        const updates = !category.id ? category :
            this.props.dirtyFields.concat('id').reduce((results, field) => ({ ...results, [field]: category[field] }), {});

        const saveCategory = (menuId, branchIdForOverride, updates, shouldSkipApi, childCategories) => {
            this.props.saveMenuCategory(menuId, branchIdForOverride, updates, shouldSkipApi, (newCategoryData) => {
                childCategories && childCategories.forEach(childCategory => {
                    childCategory.itemsInstancesToAdd = childCategory.itemsInstances.map(({ id, ...i }) => i);

                    const { menuId, id, ...cat } = convertToCategoryEntity(childCategory);
                    saveCategory(menuId, branchIdForOverride, { ...cat, primaryParentId: newCategoryData.id }, shouldSkipApi, childCategory.childCategories);
                });
            });
        };

        saveCategory(menuId, branchIdForOverride, updates, shouldSkipApi, childCategories);

        event.preventDefault();
    }

    render() {
        if (!this.props.entity || this.props.entityType !== 'menuCategory') {
            return null;
        }

        const category = this.props.entity || {};
        let typeOptions = ['primary', 'mandatory', 'addon', 'upsell', 'announcement', 'details'].map((type) => ({ value: type, text: i18next.t(`menu.${type}`) }));
        let dealOptions = [];
        let builderTypeOptions = [];

        if (category.type === 'addon') {
            builderTypeOptions = ['', 'bubbles', 'pizza'].map(value => ({ value, text: value ? i18next.t(`menu.builderType.${value}`) : '' }));
            dealOptions = ['', 'freeSelection'].map((value) => ({ value, text: value ? i18next.t(`menu.deal.${value}`) : '' }));
        }

        if (category.id) {
            if (['primary', 'upsell'].includes(category.type)) {
                typeOptions = typeOptions.filter(o => o.value === category.type);
            } else {
                typeOptions = typeOptions.filter(o => o.value === 'mandatory' || o.value === 'addon' || o.value === 'details');
            }
            if (category.options.builderType !== 'pizza') {
                builderTypeOptions = builderTypeOptions.filter(o => o.value !== 'pizza');
            }
        }

        return (
            <Form onSubmit={this.handleSubmit}>
                <Form.Field>
                    <label>{i18next.t('categoryName')}</label>
                    <input
                        placeholder={i18next.t('categoryName')}
                        value={category.name}
                        onChange={({ target }) => this.props.editEntityField('name', target.value)}
                        required
                        autoFocus={window.innerWidth > 600}
                    />
                </Form.Field>

                <Form.TextArea
                    label={i18next.t('description')}
                    placeholder={`${i18next.t('description')} (${i18next.t('maxTextLength', { length: 400 })})`}
                    maxLength={category.type === 'details' ? undefined : 400}
                    value={category.description}
                    onChange={({ target }) => this.props.editEntityField('description', target.value)}
                />

                <Form.Field>
                    <label>{i18next.t('note')}</label>
                    <input
                        placeholder={i18next.t('note')}
                        value={category.note || ''}
                        onChange={({ target }) => this.props.editEntityField('note', target.value || null)}
                    />
                </Form.Field>

                <Form.Field>
                    <label>{i18next.t('posId')}</label>
                    <input
                        placeholder={i18next.t('posId')}
                        value={category.posId || ''}
                        onChange={({ target }) => this.props.editEntityField('posId', target.value || null)}
                    />
                </Form.Field>

                <Form.Field>
                    <label>{i18next.t('type')}</label>
                    <Dropdown
                        required
                        fluid
                        selection
                        options={typeOptions}
                        value={category.type || ''}
                        onChange={(e, { value }) => {
                            this.props.editEntityField('type', value);
                            if (value !== 'upsell') {
                                this.props.editEntityField('triggers', null);
                            }
                            if (value !== 'addon') {
                                this.props.editEntityField('options', {});
                                this.props.editEntityField('limit', null);
                            }
                        }}
                    />
                </Form.Field>

                <Form.Group widths='equal'>
                    <Form.Field>
                        <Dropdown
                            placeholder={i18next.t('menu.supportedCustomerTypes')}
                            fluid
                            selection
                            multiple
                            options={['default', 'employee'].map(value => ({ value, text: i18next.t(`customerTypes.${value}`) }))}
                            value={category.options.supportedCustomerTypes || []}
                            onChange={(e, { value }) => this.props.editEntityField('options.supportedCustomerTypes', value.length ? value : null)}
                        />
                    </Form.Field>
                </Form.Group>

                <Form.Group widths='equal'>
                    <Form.Field>
                        <Dropdown
                            placeholder={i18next.t('menu.availableFor') + ['sitting', 'takeaway', 'delivery'].map(value => i18next.t(value)).join(', ')}
                            fluid
                            selection
                            multiple
                            options={['sitting', 'takeaway', 'delivery'].map(value => ({ value, text: i18next.t(value) }))}
                            value={category.options.supportedFor || []}
                            onChange={(e, { value }) => this.props.editEntityField('options.supportedFor', value.length ? value : null)}
                        />
                    </Form.Field>
                </Form.Group>

                <MenuTaxSetup entity={category} />

                {category.primaryParentId &&
                    <Form.Checkbox
                        label={i18next.t('menu.spreadItemsIfPossible')}
                        checked={!!category.options.spreadItemsIfPossible}
                        onChange={(e, { checked }) => this.props.editEntityField('options.spreadItemsIfPossible', checked || undefined)}
                    />
                }

                {category.type === 'addon' &&
                    <Form.Group widths='equal'>
                        <Form.Field>
                            <label>{i18next.t('limitItems')}</label>
                            <Input
                                fluid
                                type='number'
                                min="0"
                                value={category.limit || ''}
                                onChange={({ target }) => {
                                    this.props.editEntityField('options.limit', target.value ? Number(target.value) : undefined);
                                    this.props.editEntityField('limit', target.value ? Number(target.value) : null);
                                }}
                            />
                        </Form.Field>
                        <Form.Field>
                            <label>{i18next.t('limitPerItem')}</label>
                            <Input
                                fluid
                                type='number'
                                min="0"
                                value={category.options.limitPerItem || ''}
                                onChange={({ target }) => this.props.editEntityField('options.limitPerItem', target.value ? Number(target.value) : undefined)}
                            />
                        </Form.Field>
                    </Form.Group>
                }

                {!builderTypeOptions.length ? null :
                    <Form.Field>
                        <label>{i18next.t('menu.builderType.title')}</label>
                        <Dropdown
                            fluid
                            selection
                            disabled={category.options.builderType === 'pizza'}
                            value={category.options.builderType || ''}
                            options={builderTypeOptions}
                            onChange={(e, { value }) => this.props.editEntityField('options.builderType', value || undefined)}
                        />
                    </Form.Field>
                }

                {!dealOptions.length ? null :
                    <Form.Field>
                        <label>{i18next.t('menu.deal.title')}</label>
                        <Dropdown
                            fluid
                            selection
                            value={category.options.deal && category.options.deal.dealType || ''}
                            options={dealOptions}
                            onChange={(e, { value }) => this.props.editEntityField('options.deal', value ? { dealType: value, amount: 1 } : undefined)}
                        />
                    </Form.Field>
                }

                {dealOptions.length && category.options.deal && category.options.deal.dealType === 'freeSelection' ?
                    <Form.Field>
                        <label>{i18next.t('count')}</label>
                        <Input
                            fluid
                            type='number'
                            min="1"
                            value={category.options.deal.amount || ''}
                            onChange={({ target }) => this.props.editEntityField('options.deal', { ...category.options.deal, amount: target.value ? Number(target.value) : 1 })}
                        />
                    </Form.Field>
                    : null
                }

                {category.type === 'upsell' &&
                    (category.triggers || [{}]).map((trigger, index) => (
                        <div key={index}>
                            <Form.Field>
                                <label>{i18next.t('menu.upsells.trigger')}</label>
                                <Dropdown
                                    placeholder={i18next.t('menu.upsells.triggerPlacholder')}
                                    fluid
                                    selection
                                    value={trigger.triggerType || ''}
                                    options={['onItemAdd', 'onFinishOrder'].map(t => ({ value: t, text: i18next.t(`menu.upsells.triggerTypes.${t}`) }))}
                                    onChange={(e, { value }) => {
                                        if (category.triggers) {
                                            this.props.editEntityField(`triggers[${index}]`, { triggerType: value, conditions: {} });
                                        } else {
                                            this.props.editEntityField('triggers', [{ triggerType: value, conditions: {} }]);
                                        }
                                    }}
                                />
                            </Form.Field>
                            {trigger.triggerType === 'onItemAdd' &&
                                <Form.Field>
                                    <Dropdown
                                        placeholder={i18next.t('menu.upsells.triggerItemsPlacholder')}
                                        fluid
                                        search
                                        multiple
                                        selection
                                        value={trigger.conditions.itemsIds || []}
                                        options={this.props.triggerItemOptions}
                                        onChange={(e, { value }) => this.props.editEntityField(`triggers[${index}]`, { ...trigger, conditions: { itemsIds: value } })}
                                    />
                                    <Dropdown
                                        placeholder={i18next.t('menu.upsells.triggerCategoriesPlacholder')}
                                        fluid
                                        search
                                        multiple
                                        selection
                                        value={trigger.conditions.categoriesIds || []}
                                        options={this.props.triggerCategoryOptions}
                                        onChange={(e, { value }) => this.props.editEntityField(`triggers[${index}]`, { ...trigger, conditions: { categoriesIds: value } })}
                                    />
                                </Form.Field>
                            }
                            <Form.Field>
                                <Form.Checkbox
                                    label={i18next.t('menu.isRepeatable')}
                                    checked={!!category.options.isRepeatable}
                                    onChange={(e, { checked }) => this.props.editEntityField('options.isRepeatable', checked || undefined)}
                                />
                            </Form.Field>
                        </div>
                    ))
                }

                <Form.Field>
                    <label>{i18next.t('image')}</label>
                    <ImageInput
                        resizeMode
                        handleFileUpload={(file, mode) => this.props.uploadImage(file, 'menuItem', mode)}
                        imageUrl={category.imageUrl}
                        handleCommonSelection={value => this.props.editEntityField('imageGuid', value)}
                        unsetImage={() => {
                            this.props.editEntityField('imageGuid', null);
                            this.props.editEntityField('imageUrl', null);
                        }}
                    />
                </Form.Field>

                <AvailableDaysEditor
                    
                    object={category.availability}
                    onChange={(key, value) => this.props.editEntityField(`availability.${key}`, value)}
                />

                <Form.Field>
                    <Form.Checkbox
                        label={i18next.t('suspended')}
                        checked={category.isDisabled}
                        onChange={(e, { checked }) => this.props.editEntityField('isDisabled', checked)}
                    />
                </Form.Field>

                <Button primary type='submit' floated="right">
                    {i18next.t('save')}
                </Button>
            </Form>
        );
    }
}

MenuCategoryEditor.propTypes = {
    uploadImage: PropTypes.func,
    editEntityField: PropTypes.func,
    linkTo: PropTypes.func,
    saveMenuCategory: PropTypes.func,
    triggerItemOptions: PropTypes.array,
    triggerCategoryOptions: PropTypes.array,
    entity: PropTypes.object,
    entityType: PropTypes.string,
    dirtyFields: PropTypes.array,
    branchIdForOverride: PropTypes.string,
    
    childCategories: PropTypes.array,
};

const mapStateToProps = (state) => {
    const { entity, type: entityType, extra, dirtyFields } = state.editor;

    const triggerItemOptions = Object.values(state.businessApp.menu.items).map(i => ({ value: i.id, text: `${i.key} - ${i.name}` }));
    const triggerCategoryOptions = Object.values(state.businessApp.menu.categories).filter(c => c.type === 'primary' && c.itemsInstances.length).map(c => ({ value: c.id, text: `${c.key} - ${c.name}` }));

    return { entity, entityType, dirtyFields, triggerItemOptions, triggerCategoryOptions, ...(extra || {}) };
};

export default withRouter(connect(mapStateToProps, { saveMenuCategory, editEntityField, uploadImage })(MenuCategoryEditor));
